import { Button } from '@assembly-js/design-system';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalConfigContext } from 'src/context';
import { useAppDispatch } from 'src/hooks/useStore';
import { setStudioClientPreview } from 'src/store/ui/actions';

import { ChecklistWrapper } from './ChecklistWrapper';
import { switchToInternalPortalTab } from './internalPortalTab';

interface Props {
  progress: number;
  backgroundColor: string;
  sidebarColor: string;
  textColor: string;
}

/**
 * Client-portal variant of the Studio "Getting started" card. Reuses the shared
 * card shell, but drives its progress from the percentage passed on the preview
 * link (the client session can't read the owner's onboarding record); in place
 * of task rows it offers a "Return to CRM" action back to the dashboard, styled
 * with the portal accent.
 */
export const ClientChecklist = ({
  progress,
  backgroundColor,
  sidebarColor,
  textColor,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { brand, theme } = useContext(PortalConfigContext);
  const buttonBackgroundColor = textColor;

  // The client "Return to CRM" button uses the portal's configured accent
  // color, falling back to the portal primary when no accent is set.
  const accentColor = brand.clientAccentColor || theme.palette.primary.main;

  const handleReturnToCrm = () => {
    // Returning to the CRM ends the preview, so drop the card for the rest of the
    // session — this clears the flag that gates the client checklist. Done first
    // so the card hides even when there's no live CRM tab to switch back to.
    dispatch(setStudioClientPreview(false));
    switchToInternalPortalTab();
  };

  return (
    <ChecklistWrapper
      completionPct={progress}
      collapsible={false}
      isClient
      backgroundColor={backgroundColor}
      sidebarColor={sidebarColor}
      textColor={textColor}
    >
      <div className="box-border px-2 pb-2 pt-1">
        <Button
          label={t(
            ($) => $.page_apps.app_builder.getting_started.return_to_crm,
          )}
          variant="primary"
          size="sm"
          className="w-full"
          style={{
            backgroundColor: buttonBackgroundColor,
            borderColor: accentColor,
            color: accentColor,
          }}
          onClick={handleReturnToCrm}
        />
      </div>
    </ChecklistWrapper>
  );
};
